import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlannerPreviewViewer = ({
  plannerData,
  currentPage = 1,
  onPageChange,
  className = ""
}) => {
  const [zoomLevel, setZoomLevel] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState('individual');
  const viewerRef = useRef(null);

  // Enhanced planner pages data with color-coded placeholders
  const plannerPages = [
    {
      id: 1,
      type: 'cover',
      title: 'Cover Page',
      color: '#1e3a8a', // navy blue
      icon: 'BookOpen'
    },
    {
      id: 2,
      type: 'year-overview',
      title: 'Year at a Glance 2024',
      color: '#059669', // emerald green
      icon: 'Calendar'
    },
    {
      id: 3,
      type: 'monthly',
      title: 'January 2024',
      color: '#7c3aed', // purple
      icon: 'CalendarDays'
    },
    {
      id: 4,
      type: 'weekly',
      title: 'Week 1 - Jan 1-7',
      color: '#dc2626', // red
      icon: 'CalendarRange'
    },
    {
      id: 5,
      type: 'daily',
      title: 'Monday, Jan 1',
      color: '#ea580c', // orange
      icon: 'CalendarCheck'
    },
    {
      id: 6,
      type: 'notes',
      title: 'Notes Section',
      color: '#0891b2', // cyan
      icon: 'FileText'
    }
  ];

  const totalPages = plannerPages?.length;
  const currentPageData = plannerPages?.[currentPage - 1];

  // Generate year view data
  const generateYearViewData = () => {
    const currentYear = new Date()?.getFullYear();
    const months = [];

    for (let month = 0; month < 12; month++) {
      const monthData = {
        id: month + 1,
        name: new Date(currentYear, month)?.toLocaleString('default', { month: 'long' }),
        year: currentYear,
        monthIndex: month,
        pageNumber: month + 3, // Offset for cover, year overview
        days: []
      };

      const daysInMonth = new Date(currentYear, month + 1, 0)?.getDate();
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(currentYear, month, day);
        monthData?.days?.push({
          day,
          date,
          pageNumber: 15 + (month * 31 + day - 1), // Approximate page calculation
          isToday: isToday(date),
          dayOfWeek: date?.getDay()
        });
      }
      months?.push(monthData);
    }
    return months;
  };

  const isToday = (date) => {
    const today = new Date();
    return date?.toDateString() === today?.toDateString();
  };

  const yearViewData = generateYearViewData();

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 25, 50));
  };

  const handleResetZoom = () => {
    setZoomLevel(100);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange?.(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange?.(currentPage + 1);
    }
  };

  const handleMonthClick = (monthData) => {
    // Navigate to monthly page
    onPageChange?.(monthData?.pageNumber);
    setViewMode('individual');
  };

  const handleDayClick = (dayData) => {
    // Navigate to daily page
    onPageChange?.(dayData?.pageNumber);
    setViewMode('individual');
  };

  const handleKeyNavigation = (e) => {
    if (e?.key === 'ArrowLeft') handlePrevPage();
    if (e?.key === 'ArrowRight') handleNextPage();
    if (e?.key === 'Escape') setIsFullscreen(false);
  };

  useEffect(() => {
    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyNavigation);
      return () => document.removeEventListener('keydown', handleKeyNavigation);
    }
  }, [isFullscreen, currentPage]);

  const renderYearView = () =>
  <div className="grid grid-cols-3 md:grid-cols-4 gap-4 p-6 bg-white">
      {yearViewData?.map((month) =>
    <div key={month?.id} className="bg-card border border-border rounded-lg p-3 hover:shadow-md transition-shadow">
          {/* Month header - clickable */}
          <button
        onClick={() => handleMonthClick(month)}
        className="w-full text-left mb-2 hover:text-primary transition-colors">

            <h3 className="font-heading font-semibold text-sm text-center">
              {month?.name} {month?.year}
            </h3>
          </button>
          
          {/* Mini calendar grid */}
          <div className="grid grid-cols-7 gap-0.5 text-xs">
            {/* Days of week headers */}
            {['S', 'M', 'T', 'W', 'T', 'F', 'S']?.map((day) =>
        <div key={day} className="text-center text-muted-foreground font-medium p-0.5">
                {day}
              </div>
        )}
            
            {/* Empty cells for days before month starts */}
            {Array.from({ length: month?.days?.[0]?.dayOfWeek || 0 })?.map((_, index) =>
        <div key={`empty-${index}`} className="p-0.5"></div>
        )}
            
            {/* Month days - clickable */}
            {month?.days?.map((dayData) =>
        <button
          key={dayData?.day}
          onClick={() => handleDayClick(dayData)}
          className={`
                  p-0.5 text-center transition-colors hover:bg-primary hover:text-primary-foreground rounded
                  ${dayData?.isToday ? 'bg-primary text-primary-foreground font-bold' : 'hover:bg-muted'}
                `}
          title={`Jump to ${month?.name} ${dayData?.day}, ${month?.year}`}>

                {dayData?.day}
              </button>
        )}
          </div>
          
          {/* Month navigation info */}
          <div className="text-xs text-muted-foreground text-center mt-2">
            Page {month?.pageNumber}
          </div>
        </div>
    )}
    </div>;


  return (
    <div className={`bg-card border border-border rounded-lg overflow-hidden ${className}`}>
      {/* Viewer Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
        <div className="flex items-center space-x-3">
          <h3 className="font-heading font-semibold text-foreground">
            {viewMode === 'year' ? 'Year Overview 2024' : currentPageData?.title}
          </h3>
          <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-caption rounded-full">
            {viewMode === 'year' ? 'year-navigation' : currentPageData?.type}
          </span>
        </div>

        <div className="flex items-center space-x-2">
          {/* View Mode Toggle */}
          <div className="flex items-center space-x-1 bg-card border border-border rounded-md">
            <Button
              variant={viewMode === 'individual' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('individual')}
              iconName="FileText">

              Pages
            </Button>
            <Button
              variant={viewMode === 'year' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('year')}
              iconName="Calendar">

              Year View
            </Button>
          </div>

          {/* Zoom Controls - only for individual pages */}
          {viewMode === 'individual' &&
          <>
              <div className="flex items-center space-x-1 bg-card border border-border rounded-md">
                <Button
                variant="ghost"
                size="icon"
                onClick={handleZoomOut}
                disabled={zoomLevel <= 50}
                iconName="ZoomOut" />

                <Button
                variant="ghost"
                size="icon"
                onClick={handleResetZoom}
                iconName="RotateCcw" />

                <Button
                variant="ghost"
                size="icon"
                onClick={handleZoomIn}
                disabled={zoomLevel >= 200}
                iconName="ZoomIn" />

              </div>

              <span className="font-caption text-sm text-muted-foreground min-w-[4rem] text-center">
                {zoomLevel}%
              </span>
            </>
          }

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsFullscreen(!isFullscreen)}
            iconName={isFullscreen ? 'Minimize2' : 'Maximize2'} />

        </div>
      </div>
      {/* Preview Area */}
      <div
        ref={viewerRef}
        className={`
          relative bg-gray-100 overflow-auto
          ${isFullscreen ? 'fixed inset-0 z-50 bg-black/90' : 'h-[600px]'}
        `}
      >
        {viewMode === 'year' ? (
          <div className="flex items-center justify-center min-h-full">
            {renderYearView()}
          </div>
        ) : (
          <div className="flex items-center justify-center min-h-full p-8">
            <div
              className="relative bg-white shadow-2xl rounded-lg overflow-visible transition-transform duration-200"
              style={{
                transform: `scale(${zoomLevel / 100})`,
                transformOrigin: 'center center',
                width: isFullscreen ? '70vw' : '100%',
                maxWidth: '800px',
                aspectRatio: '8.5 / 11'
              }}
            >
              {/* Color-coded placeholder preview */}
              <div 
                className="w-full h-full flex flex-col items-center justify-center p-8"
                style={{ backgroundColor: currentPageData?.color }}
              >
                <Icon name={currentPageData?.icon} size={64} className="text-white mb-4 opacity-80" />
                <h3 className="text-white text-2xl font-heading font-bold text-center mb-2">
                  {currentPageData?.title}
                </h3>
                <p className="text-white/80 text-sm font-caption text-center">
                  {currentPageData?.type?.replace('-', ' ')?.toUpperCase()}
                </p>
              </div>

              {/* Page Overlay Info */}
              <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full">
                <span className="font-caption text-xs">
                  Page {currentPage} of {totalPages}
                </span>
              </div>

              {/* Navigation Arrows - only for individual pages */}
              <button
                onClick={handlePrevPage}
                disabled={currentPage <= 1}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/70 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:bg-black/90 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Icon name="ChevronLeft" size={20} />
              </button>

              <button
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/70 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:bg-black/90 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Icon name="ChevronRight" size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Fullscreen Close Button */}
        {isFullscreen &&
        <button
          onClick={() => setIsFullscreen(false)}
          className="absolute top-4 right-4 w-10 h-10 bg-black/70 text-white rounded-full flex items-center justify-center hover:bg-black/90 transition-colors">

            <Icon name="X" size={20} />
          </button>
        }
      </div>
      {/* Page Navigation - only for individual pages */}
      {viewMode === 'individual' &&
      <div className="flex items-center justify-between p-4 border-t border-border">
          <Button
          variant="outline"
          onClick={handlePrevPage}
          disabled={currentPage <= 1}
          iconName="ChevronLeft"
          iconPosition="left"
          size="sm">

            Previous
          </Button>

          <div className="flex items-center space-x-2">
            {plannerPages?.map((page, index) =>
          <button
            key={page?.id}
            onClick={() => onPageChange?.(index + 1)}
            className={`
                  w-8 h-8 rounded-full text-xs font-caption transition-all duration-200
                  ${currentPage === index + 1 ?
            'bg-primary text-primary-foreground' :
            'bg-muted text-muted-foreground hover:bg-muted-foreground hover:text-muted'}
                `}>

                {index + 1}
              </button>
          )}
          </div>

          <Button
          variant="outline"
          onClick={handleNextPage}
          disabled={currentPage >= totalPages}
          iconName="ChevronRight"
          iconPosition="right"
          size="sm">

            Next
          </Button>
        </div>
      }
      {/* Year View Instructions */}
      {viewMode === 'year' &&
      <div className="p-4 border-t border-border bg-muted/20">
          <div className="text-center space-y-2">
            <h4 className="font-heading font-semibold text-foreground">Interactive Year Navigation</h4>
            <p className="text-sm text-muted-foreground">
              • Click on month names to jump to monthly pages
              • Click on individual days to jump to daily pages  
              • Today is highlighted in blue
            </p>
          </div>
        </div>
      }
    </div>
  );

};

export default PlannerPreviewViewer;